import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  booleanAttribute,
} from '@angular/core';
import { DsAccordionComponent } from '../containment/ds-accordion.component';
import { DsPaginatorComponent } from '../navigation/ds-paginator.component';
import { DsCheckComponent } from '../selection/ds-check.component';

export type DsTableKey = string | number;
export type DsTableAppearance = 'outlined' | 'filled';
export type DsTableSize = 'xs' | 's' | 'm' | 'l';
export type DsTableShadow = 'none' | 'l1' | 'l2' | 'l3';
export type DsTableCellEmphasis = 'light' | 'strong';
export type DsTableResponsiveMode = 'accordion' | 'stacked' | 'key-value';
export type DsTablePaginationMode = 'client' | 'server';
export type DsTableHorizontalAlignment = 'left' | 'center' | 'right';
export type DsTableVerticalAlignment = 'top' | 'center' | 'bottom';

export interface DsTableCellStyle {
  emphasis?: DsTableCellEmphasis;
  divider?: boolean;
  invert?: boolean;
  size?: DsTableSize;
  background?: string;
  textColor?: string;
  horizontalAlignment?: DsTableHorizontalAlignment;
  verticalAlignment?: DsTableVerticalAlignment;
}

export interface DsTableColumn<T> {
  key: string;
  header: string;
  value: (row: T) => string;
  width?: string;
  headerCellStyle?: DsTableCellStyle;
  cellStyle?: DsTableCellStyle | ((row: T) => DsTableCellStyle | undefined);
}

export interface DsTableCellContext<T> {
  $implicit: T;
  column: DsTableColumn<T>;
}

/** Custom cell renderer associated with a column key. */
@Directive({
  selector: 'ng-template[dsTableCell]',
  standalone: true,
})
export class DsTableCellDefDirective<T = unknown> {
  @Input('dsTableCell') key = '';

  constructor(readonly template: TemplateRef<DsTableCellContext<T>>) {}

  static ngTemplateContextGuard<T>(
    _directive: DsTableCellDefDirective<T>,
    _context: unknown,
  ): _context is DsTableCellContext<T> {
    return true;
  }
}

@Component({
  selector: 'th[ds-header-cell]',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content />',
  styles: [`
    :host {
      height: var(--ds-table-cell-row-height, var(--ds-table-resolved-row-height));
      padding:
        var(--ds-table-cell-padding-y-override, var(--ds-table-resolved-cell-padding-y))
        var(--ds-table-cell-padding-x-override, var(--ds-table-resolved-cell-padding-x));
      box-sizing: border-box;
      color: var(--ds-table-resolved-header-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-header-font-size);
      font-weight: var(--ds-table-header-font-weight);
      line-height: var(--ds-table-header-line-height);
      border-bottom: var(--ds-table-cell-divider-width, var(--ds-table-border-width)) solid
        var(--ds-table-cell-divider-color, var(--ds-table-divider-color));
      text-align: left;
      vertical-align: middle;
    }
  `],
})
export class DsHeaderCellComponent {}

@Component({
  selector: 'td[ds-cell]',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content />',
  styles: [`
    :host {
      height: var(--ds-table-cell-row-height, var(--ds-table-resolved-row-height));
      padding:
        var(--ds-table-cell-padding-y-override, var(--ds-table-resolved-cell-padding-y))
        var(--ds-table-cell-padding-x-override, var(--ds-table-resolved-cell-padding-x));
      box-sizing: border-box;
      color: var(--ds-table-resolved-cell-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-cell-font-size);
      font-weight: var(--ds-table-cell-font-weight);
      line-height: var(--ds-table-cell-line-height);
      border-bottom: var(--ds-table-cell-divider-width, 0px) solid
        var(--ds-table-cell-divider-color, var(--ds-table-divider-color));
      text-align: left;
      vertical-align: middle;
    }
  `],
})
export class DsCellComponent {}

/**
 * Paginated data table that can opt into accordion or always-expanded stacked rows.
 * In `server` pagination mode, `rows` contains the current page and `totalItems` contains
 * the total number of records reported by the data source.
 * `page` and `pageChange` are zero-based; the visible labels remain one-based.
 */
@Component({
  selector: 'ds-table',
  standalone: true,
  imports: [
    CommonModule,
    DsAccordionComponent,
    DsCellComponent,
    DsCheckComponent,
    DsHeaderCellComponent,
    DsPaginatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [class]="hostClasses"
      [style.--ds-table-resolved-background]="resolvedBackground"
      [style.--ds-table-resolved-border-color]="resolvedBorderColor"
      [style.--ds-table-resolved-header-background]="resolvedHeaderBackground"
      [style.--ds-table-resolved-cell-background]="resolvedCellBackground"
      [style.--ds-table-resolved-collapsed-background]="resolvedCollapsedBackground"
      [style.--ds-table-resolved-header-text-color]="resolvedHeaderTextColor"
      [style.--ds-table-resolved-cell-text-color]="resolvedCellTextColor"
    >
      @if (useCompactLayout) {
        <div
          class="ds-table__compact"
          [class.ds-table__compact--key-value]="responsiveMode === 'key-value'"
          role="list"
        >
          @for (row of visibleRows; track rowIdentity(row, $index)) {
            <div class="ds-table__compact-row" role="listitem">
              @if (responsiveMode === 'accordion') {
                <ds-accordion
                  class="ds-table__accordion"
                  [class.ds-table__accordion--expanded]="isExpanded(row, $index)"
                  [shadow]="resolvedShadow"
                  [border]="showBorder"
                  [borderColor]="resolvedBorderColor"
                  [expanded]="isExpanded(row, $index)"
                  (expandedChange)="setExpanded(row, $index, $event)"
                >
                  <span
                    ds-accordion-header
                    class="ds-table__compact-pair ds-table__compact-header"
                    [class.ds-table__compact-header--selectable]="selectable"
                  >
                    <strong class="ds-table__compact-label">{{ columns[0].header }}</strong>
                    <span class="ds-table__compact-value">
                      @if (cellTemplate(columns[0].key); as customCell) {
                        <ng-container
                          [ngTemplateOutlet]="customCell"
                          [ngTemplateOutletContext]="cellContext(row, columns[0])"
                        />
                      } @else {
                        {{ columns[0].value(row) }}
                      }
                    </span>
                  </span>

                  <div ds-accordion-content>
                    @for (column of compactColumns; track column.key) {
                      <div class="ds-table__compact-pair">
                        <strong class="ds-table__compact-label">{{ column.header }}</strong>
                        <span class="ds-table__compact-value">
                          @if (cellTemplate(column.key); as customCell) {
                            <ng-container
                              [ngTemplateOutlet]="customCell"
                              [ngTemplateOutletContext]="cellContext(row, column)"
                            />
                          } @else {
                            {{ column.value(row) }}
                          }
                        </span>
                      </div>
                    }
                  </div>
                </ds-accordion>
              } @else if (responsiveMode === 'stacked') {
                <div class="ds-table__stacked-card">
                  <div class="ds-table__stacked-header">
                    <div
                      class="ds-table__compact-pair ds-table__compact-header"
                      [class.ds-table__compact-header--selectable]="selectable"
                    >
                      <strong class="ds-table__compact-label">{{ columns[0].header }}</strong>
                      <span class="ds-table__compact-value">
                        @if (cellTemplate(columns[0].key); as customCell) {
                          <ng-container
                            [ngTemplateOutlet]="customCell"
                            [ngTemplateOutletContext]="cellContext(row, columns[0])"
                          />
                        } @else {
                          {{ columns[0].value(row) }}
                        }
                      </span>
                    </div>
                  </div>

                  <div class="ds-table__stacked-content">
                    @for (column of compactColumns; track column.key) {
                      <div class="ds-table__compact-pair">
                        <strong class="ds-table__compact-label">{{ column.header }}</strong>
                        <span class="ds-table__compact-value">
                          @if (cellTemplate(column.key); as customCell) {
                            <ng-container
                              [ngTemplateOutlet]="customCell"
                              [ngTemplateOutletContext]="cellContext(row, column)"
                            />
                          } @else {
                            {{ column.value(row) }}
                          }
                        </span>
                      </div>
                    }
                  </div>
                </div>
              } @else {
                <div class="ds-table__key-value-card">
                  <div
                    class="ds-table__key-value-content"
                    [class.ds-table__key-value-content--selectable]="selectable"
                  >
                    @for (column of columns; track column.key) {
                      <span
                        class="ds-table__key-value-label"
                        [style.color]="keyValueLabelTextColor(column.headerCellStyle)"
                      >{{ keyValueLabel(column.header) }}</span>
                      <span
                        class="ds-table__key-value-value"
                        [style.color]="cellTextColor(resolvedBodyCellStyle(column, row), 'body')"
                      >
                        @if (cellTemplate(column.key); as customCell) {
                          <ng-container
                            [ngTemplateOutlet]="customCell"
                            [ngTemplateOutletContext]="cellContext(row, column)"
                          />
                        } @else {
                          {{ column.value(row) }}
                        }
                      </span>
                    }
                  </div>
                </div>
              }

              @if (selectable) {
                <ds-check
                  class="ds-table__compact-check"
                  [checked]="isSelected(row, $index)"
                  [ariaLabel]="isSelected(row, $index) ? 'Deseleccionar fila' : 'Seleccionar fila'"
                  (checkedChange)="setSelected(row, $index, $event)"
                />
              }
            </div>
          }
        </div>
      } @else {
        <div class="ds-table__frame">
          <table>
            <thead>
              <tr>
                @if (selectable) {
                  <th ds-header-cell class="ds-table__selection-cell">
                    <ds-check
                      [checked]="allVisibleSelected"
                      [ariaLabel]="allVisibleSelected ? 'Deseleccionar filas visibles' : 'Seleccionar filas visibles'"
                      (checkedChange)="setVisibleSelected($event)"
                    />
                  </th>
                }
                @for (column of columns; track column.key) {
                  <th
                    ds-header-cell
                    scope="col"
                    [style.width]="column.width || null"
                    [style.background]="cellBackground(column.headerCellStyle, 'header')"
                    [style.color]="cellTextColor(column.headerCellStyle, 'header')"
                    [style.text-align]="cellHorizontalAlignment(column.headerCellStyle)"
                    [style.vertical-align]="cellVerticalAlignment(column.headerCellStyle)"
                    [style.--ds-table-cell-divider-width]="cellDividerWidth(column.headerCellStyle, true)"
                    [style.--ds-table-cell-divider-color]="cellDividerColor(column.headerCellStyle)"
                    [style.--ds-table-cell-row-height]="cellRowHeight(column.headerCellStyle)"
                    [style.--ds-table-cell-padding-x-override]="cellPaddingX(column.headerCellStyle)"
                    [style.--ds-table-cell-padding-y-override]="cellPaddingY(column.headerCellStyle)"
                  >
                    {{ column.header }}
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of visibleRows; track rowIdentity(row, $index); let isLastRow = $last) {
                <tr>
                  @if (selectable) {
                    <td ds-cell class="ds-table__selection-cell">
                      <ds-check
                        [checked]="isSelected(row, $index)"
                        [ariaLabel]="isSelected(row, $index) ? 'Deseleccionar fila' : 'Seleccionar fila'"
                        (checkedChange)="setSelected(row, $index, $event)"
                      />
                    </td>
                  }
                  @for (column of columns; track column.key) {
                    <td
                      ds-cell
                      [style.background]="cellBackground(resolvedBodyCellStyle(column, row), 'body')"
                      [style.color]="cellTextColor(resolvedBodyCellStyle(column, row), 'body')"
                      [style.text-align]="cellHorizontalAlignment(resolvedBodyCellStyle(column, row))"
                      [style.vertical-align]="cellVerticalAlignment(resolvedBodyCellStyle(column, row))"
                      [style.--ds-table-cell-divider-width]="cellDividerWidth(resolvedBodyCellStyle(column, row), !isLastRow)"
                      [style.--ds-table-cell-divider-color]="cellDividerColor(resolvedBodyCellStyle(column, row))"
                      [style.--ds-table-cell-row-height]="cellRowHeight(resolvedBodyCellStyle(column, row))"
                      [style.--ds-table-cell-padding-x-override]="cellPaddingX(resolvedBodyCellStyle(column, row))"
                      [style.--ds-table-cell-padding-y-override]="cellPaddingY(resolvedBodyCellStyle(column, row))"
                    >
                      @if (cellTemplate(column.key); as customCell) {
                        <ng-container
                          [ngTemplateOutlet]="customCell"
                          [ngTemplateOutletContext]="cellContext(row, column)"
                        />
                      } @else {
                        {{ column.value(row) }}
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (pageCount > 1) {
        <ds-paginator
          class="ds-table__pagination"
          [pageCount]="pageCount"
          [page]="safePage"
          ariaLabel="Paginación de la tabla"
          (pageChange)="setPage($event)"
        />
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .ds-table {
      --ds-table-resolved-row-height: var(--ds-table-l-row-height);
      --ds-table-resolved-cell-padding-x: var(--ds-table-l-cell-padding-x);
      --ds-table-resolved-cell-padding-y: var(--ds-table-l-cell-padding-y);
      --ds-table-resolved-selection-column-width: var(--ds-table-l-selection-column-width);
      --ds-table-resolved-outer-border-width: var(--ds-table-border-width);
      --ds-table-resolved-divider-width: var(--ds-table-border-width);
      --ds-table-resolved-body-divider-width: var(--ds-table-border-width);
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: var(--ds-table-content-gap);
    }

    .ds-table--without-dividers {
      --ds-table-resolved-divider-width: 0px;
      --ds-table-resolved-body-divider-width: 0px;
    }

    .ds-table--bordered {
      --ds-table-resolved-outer-border-width: var(--ds-table-border-width);
    }

    .ds-table--borderless {
      --ds-table-resolved-outer-border-width: 0px;
    }

    .ds-table--size-xs {
      --ds-table-resolved-row-height: var(--ds-table-xs-row-height);
      --ds-table-resolved-cell-padding-x: var(--ds-table-xs-cell-padding-x);
      --ds-table-resolved-cell-padding-y: var(--ds-table-xs-cell-padding-y);
      --ds-table-resolved-selection-column-width: var(--ds-table-xs-selection-column-width);
    }

    .ds-table--size-s {
      --ds-table-resolved-row-height: var(--ds-table-s-row-height);
      --ds-table-resolved-cell-padding-x: var(--ds-table-s-cell-padding-x);
      --ds-table-resolved-cell-padding-y: var(--ds-table-s-cell-padding-y);
      --ds-table-resolved-selection-column-width: var(--ds-table-s-selection-column-width);
    }

    .ds-table--size-m {
      --ds-table-resolved-row-height: var(--ds-table-m-row-height);
      --ds-table-resolved-cell-padding-x: var(--ds-table-m-cell-padding-x);
      --ds-table-resolved-cell-padding-y: var(--ds-table-m-cell-padding-y);
      --ds-table-resolved-selection-column-width: var(--ds-table-m-selection-column-width);
    }

    .ds-table__frame {
      width: 100%;
      overflow: hidden;
      border: var(--ds-table-resolved-outer-border-width) solid var(--ds-table-resolved-border-color);
      border-radius: var(--ds-table-border-radius);
      background: var(--ds-table-resolved-background);
      box-sizing: border-box;
    }

    .ds-table--shadow-l1 .ds-table__frame,
    .ds-table--shadow-l1 .ds-table__stacked-card,
    .ds-table--shadow-l1 .ds-table__key-value-card {
      box-shadow: var(--ds-table-shadow-l1);
    }

    .ds-table--shadow-l2 .ds-table__frame,
    .ds-table--shadow-l2 .ds-table__stacked-card,
    .ds-table--shadow-l2 .ds-table__key-value-card {
      box-shadow: var(--ds-table-shadow-l2);
    }

    .ds-table--shadow-l3 .ds-table__frame,
    .ds-table--shadow-l3 .ds-table__stacked-card,
    .ds-table--shadow-l3 .ds-table__key-value-card {
      box-shadow: var(--ds-table-shadow-l3);
    }

    .ds-table--shadow-none .ds-table__frame,
    .ds-table--shadow-none .ds-table__stacked-card,
    .ds-table--shadow-none .ds-table__key-value-card {
      box-shadow: none;
    }

    table {
      width: 100%;
      border-spacing: 0;
      table-layout: fixed;
    }

    thead {
      background: var(--ds-table-resolved-header-background);
    }

    thead .ds-table__selection-cell {
      border-bottom: var(--ds-table-resolved-divider-width) solid var(--ds-table-divider-color);
    }

    thead th {
      background: var(--ds-table-resolved-header-background);
    }

    tbody td {
      background: var(--ds-table-resolved-cell-background);
    }

    tbody tr:not(:last-child) .ds-table__selection-cell {
      border-bottom: var(--ds-table-resolved-body-divider-width) solid var(--ds-table-divider-color);
    }

    tr {
      display: table-row;
    }

    .ds-table__selection-cell {
      width: var(--ds-table-resolved-selection-column-width);
      padding: 0;
      text-align: center;
    }

    .ds-table__selection-cell ds-check {
      vertical-align: middle;
    }

    .ds-table__compact {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: var(--ds-table-mobile-row-gap);
    }

    .ds-table__compact--key-value {
      gap: var(--ds-table-mobile-key-value-card-gap);
    }

    .ds-table__compact-row {
      position: relative;
      width: 100%;
    }

    .ds-table__accordion {
      --ds-accordion-background:
        linear-gradient(
          var(--ds-table-resolved-cell-background),
          var(--ds-table-resolved-cell-background)
        ),
        var(--ds-table-resolved-background);
      --ds-accordion-header-background: var(--ds-table-resolved-collapsed-background);
      --ds-accordion-divider-color: var(--ds-table-divider-color);
      --ds-accordion-title-color: var(--ds-table-resolved-header-text-color);
      --ds-accordion-content-color: var(--ds-table-resolved-cell-text-color);
      --ds-accordion-border-radius: var(--ds-table-border-radius);
      --ds-accordion-divider-width: var(--ds-table-resolved-divider-width);
      --ds-accordion-title-font-size: var(--ds-table-header-font-size);
      --ds-accordion-title-line-height: var(--ds-table-header-line-height);
      --ds-accordion-title-font-weight: var(--ds-table-header-font-weight);
      --ds-accordion-content-gap: var(--ds-table-mobile-content-gap);
    }

    .ds-table__accordion--expanded {
      --ds-accordion-header-background: var(--ds-table-resolved-header-background);
    }

    .ds-table--filled .ds-table__accordion,
    .ds-table--filled .ds-table__accordion--expanded {
      --ds-accordion-header-background: var(--ds-table-cell-light-background);
    }

    .ds-table__stacked-card {
      overflow: hidden;
      border: var(--ds-table-resolved-outer-border-width) solid var(--ds-table-resolved-border-color);
      border-radius: var(--ds-table-border-radius);
      background: var(--ds-table-resolved-background);
      box-sizing: border-box;
    }

    .ds-table__key-value-card {
      overflow: hidden;
      border: var(--ds-table-resolved-outer-border-width) solid var(--ds-table-resolved-border-color);
      border-radius: var(--ds-table-border-radius);
      background: var(--ds-table-resolved-cell-background);
      box-sizing: border-box;
    }

    .ds-table__key-value-content {
      display: grid;
      grid-template-columns: var(--ds-table-mobile-key-value-label-width) minmax(0, 1fr);
      column-gap: var(--ds-table-mobile-content-gap);
      row-gap: var(--ds-table-mobile-key-value-row-gap);
      padding:
        var(--ds-table-mobile-content-padding-y)
        var(--ds-table-mobile-content-padding-x);
      color: var(--ds-table-resolved-cell-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-cell-font-size);
      line-height: var(--ds-table-cell-line-height);
      overflow-wrap: anywhere;
    }

    .ds-table__key-value-content--selectable {
      padding-left: calc(
        var(--ds-table-mobile-content-padding-x) + var(--ds-table-mobile-selection-inset)
      );
    }

    .ds-table__key-value-label {
      align-self: start;
      justify-self: start;
      color: var(--ds-table-mobile-key-value-label-text-color);
      font-weight: var(--ds-table-mobile-key-value-label-font-weight);
      white-space: nowrap;
    }

    .ds-table__key-value-value {
      min-width: 0;
      align-self: start;
      justify-self: stretch;
      color: var(--ds-table-resolved-cell-text-color);
      font-weight: var(--ds-table-mobile-key-value-value-font-weight);
      text-align: left;
    }

    .ds-table__stacked-header {
      padding: var(--ds-table-resolved-cell-padding-y) var(--ds-table-resolved-cell-padding-x);
      border-bottom: var(--ds-table-resolved-divider-width) solid var(--ds-table-divider-color);
      background: var(--ds-table-resolved-header-background);
    }

    .ds-table__stacked-content {
      display: flex;
      flex-direction: column;
      gap: var(--ds-table-mobile-content-gap);
      padding: var(--ds-table-resolved-cell-padding-y) var(--ds-table-resolved-cell-padding-x);
      background: var(--ds-table-resolved-cell-background);
      overflow-wrap: anywhere;
    }

    .ds-table__compact-pair {
      display: flex;
      width: 100%;
      min-width: 0;
      align-items: center;
      justify-content: space-between;
      gap: var(--ds-table-mobile-content-gap);
      color: var(--ds-table-resolved-cell-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-cell-font-size);
      font-weight: var(--ds-table-cell-font-weight);
      line-height: var(--ds-table-cell-line-height);
    }

    .ds-table__compact-header {
      min-height: var(--ds-table-mobile-header-content-min-height);
    }

    .ds-table__compact-header--selectable {
      padding-left: var(--ds-table-mobile-selection-inset);
    }

    .ds-table__compact-label {
      color: var(--ds-table-resolved-header-text-color);
      font-weight: var(--ds-table-header-font-weight);
    }

    .ds-table__compact-value {
      min-width: 0;
      text-align: right;
    }

    .ds-table__compact-check {
      position: absolute;
      z-index: var(--ds-table-mobile-check-z-index);
      top: var(--ds-table-mobile-check-inset-y);
      left: var(--ds-table-mobile-check-inset-x);
    }

    .ds-table__pagination {
      align-self: flex-end;
    }
  `],
})
export class DsTableComponent<T = unknown> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() columns: readonly DsTableColumn<T>[] = [];
  @Input() rows: readonly T[] = [];
  @Input() rowKey: (row: T, index: number) => DsTableKey = (_row, index) => index;
  @Input() itemsPerPage = 10;
  @Input() page = 0;
  @Input() paginationMode: DsTablePaginationMode = 'client';
  @Input() totalItems = 0;
  @Input() selectable = false;
  @Input() selectedKeys: readonly DsTableKey[] = [];
  @Input() responsive = false;
  @Input() responsiveBreakpoint: number | null = null;
  @Input() responsiveMode: DsTableResponsiveMode = 'accordion';
  @Input() appearance: DsTableAppearance = 'outlined';
  @Input() size: DsTableSize = 'l';
  @Input({ transform: booleanAttribute }) divider = true;
  @Input() horizontalAlignment: DsTableHorizontalAlignment = 'left';
  @Input() verticalAlignment: DsTableVerticalAlignment = 'center';
  @Input() background: string | null = null;
  @Input() border: boolean | string | null = null;
  @Input() borderColor: string | null = null;
  @Input() shadow: DsTableShadow | null = null;
  @Input() expandedByDefault = false;
  @Input() allowMultipleExpanded = false;

  @Output() readonly pageChange = new EventEmitter<number>();
  @Output() readonly selectedKeysChange = new EventEmitter<readonly DsTableKey[]>();

  @ContentChildren(DsTableCellDefDirective)
  private cellDefs?: QueryList<DsTableCellDefDirective<T>>;

  private readonly expandedKeys = new Set<DsTableKey>();
  private resizeObserver?: ResizeObserver;
  private compactLayout = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? this.host.nativeElement.clientWidth;
      this.zone.run(() => this.updateCompactLayout(width));
    });
    this.resizeObserver.observe(this.host.nativeElement);
    this.updateCompactLayout(this.host.nativeElement.clientWidth);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.itemsPerPage < 1) this.itemsPerPage = 1;
    this.page = this.safePage;

    if (
      changes['rows'] ||
      changes['rowKey'] ||
      changes['expandedByDefault'] ||
      changes['allowMultipleExpanded']
    ) {
      if (changes['expandedByDefault']) this.expandedKeys.clear();
      this.syncExpandedKeys();
    }
    if (changes['responsive'] || changes['responsiveBreakpoint']) {
      this.updateCompactLayout(this.host.nativeElement.clientWidth);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get useCompactLayout(): boolean {
    return this.responsive && this.compactLayout && this.columns.length > 0;
  }

  get resolvedBackground(): string {
    return this.background ?? (
      this.appearance === 'outlined'
        ? 'var(--ds-table-outlined-background)'
        : 'var(--ds-table-background)'
    );
  }

  get showBorder(): boolean {
    if (this.border === null || this.border === undefined) return this.appearance === 'outlined';
    return this.border === true || this.border === '' || this.border === 'true';
  }

  get resolvedBorderColor(): string {
    return this.borderColor ?? 'var(--ds-table-border-color)';
  }

  get resolvedShadow(): DsTableShadow {
    return this.shadow ?? 'none';
  }

  get resolvedHeaderBackground(): string {
    return this.appearance === 'filled'
      ? 'var(--ds-table-header-light-background)'
      : 'var(--ds-table-header-background)';
  }

  get resolvedCellBackground(): string {
    return this.appearance === 'filled'
      ? 'var(--ds-table-cell-strong-background)'
      : 'var(--ds-table-outlined-background)';
  }

  get resolvedCollapsedBackground(): string {
    return this.background ?? 'var(--ds-table-accordion-collapsed-background)';
  }

  get resolvedHeaderTextColor(): string {
    return 'var(--ds-table-header-text-color)';
  }

  get resolvedCellTextColor(): string {
    return 'var(--ds-table-cell-text-color)';
  }

  get hostClasses(): string {
    return [
      'ds-table',
      `ds-table--${this.appearance}`,
      `ds-table--size-${this.size}`,
      this.divider ? 'ds-table--with-dividers' : 'ds-table--without-dividers',
      this.showBorder ? 'ds-table--bordered' : 'ds-table--borderless',
      `ds-table--shadow-${this.resolvedShadow}`,
    ].join(' ');
  }

  get pageCount(): number {
    const itemCount = this.paginationMode === 'server'
      ? this.normalizedTotalItems
      : this.rows.length;
    return itemCount === 0 ? 0 : Math.ceil(itemCount / this.itemsPerPage);
  }

  get safePage(): number {
    return Math.min(Math.max(this.page, 0), Math.max(this.pageCount - 1, 0));
  }

  get visibleRows(): readonly T[] {
    if (this.paginationMode === 'server') return this.rows;
    const start = this.safePage * this.itemsPerPage;
    return this.rows.slice(start, start + this.itemsPerPage);
  }

  get compactColumns(): readonly DsTableColumn<T>[] {
    return this.columns.slice(1);
  }

  get allVisibleSelected(): boolean {
    const selected = new Set(this.selectedKeys);
    const keys = this.visibleRows.map((row, index) => this.keyFor(row, index));
    return keys.length > 0 && keys.every(key => selected.has(key));
  }

  rowIdentity(row: T, visibleIndex: number): DsTableKey {
    return this.keyFor(row, visibleIndex);
  }

  isSelected(row: T, visibleIndex: number): boolean {
    return new Set(this.selectedKeys).has(this.keyFor(row, visibleIndex));
  }

  setSelected(row: T, visibleIndex: number, checked: boolean): void {
    const next = new Set(this.selectedKeys);
    const key = this.keyFor(row, visibleIndex);
    checked ? next.add(key) : next.delete(key);
    this.selectedKeys = [...next];
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  setVisibleSelected(checked: boolean): void {
    const next = new Set(this.selectedKeys);
    this.visibleRows.forEach((row, index) => {
      const key = this.keyFor(row, index);
      checked ? next.add(key) : next.delete(key);
    });
    this.selectedKeys = [...next];
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  isExpanded(row: T, visibleIndex: number): boolean {
    return this.expandedKeys.has(this.keyFor(row, visibleIndex));
  }

  setExpanded(row: T, visibleIndex: number, expanded: boolean): void {
    const key = this.keyFor(row, visibleIndex);
    if (expanded && !this.allowMultipleExpanded) this.expandedKeys.clear();
    expanded ? this.expandedKeys.add(key) : this.expandedKeys.delete(key);
    this.cdr.markForCheck();
  }

  setPage(nextPage: number): void {
    const safeNextPage = Math.min(Math.max(nextPage, 0), Math.max(this.pageCount - 1, 0));
    if (safeNextPage === this.safePage) return;
    this.page = safeNextPage;
    this.pageChange.emit(safeNextPage);
    this.cdr.markForCheck();
  }

  cellTemplate(key: string): TemplateRef<DsTableCellContext<T>> | null {
    return this.cellDefs?.find(definition => definition.key === key)?.template ?? null;
  }

  cellContext(row: T, column: DsTableColumn<T>): DsTableCellContext<T> {
    return { $implicit: row, column };
  }

  resolvedBodyCellStyle(column: DsTableColumn<T>, row: T): DsTableCellStyle | undefined {
    return typeof column.cellStyle === 'function' ? column.cellStyle(row) : column.cellStyle;
  }

  cellBackground(style: DsTableCellStyle | undefined, role: 'header' | 'body'): string {
    if (style?.background !== undefined) return style.background;
    const emphasis = style?.emphasis ?? this.defaultEmphasis(role);
    const invert = style?.invert === true;
    if (role === 'header') {
      if (invert) {
        return emphasis === 'strong'
          ? 'var(--ds-table-invert-header-strong-background)'
          : 'var(--ds-table-invert-header-light-background)';
      }
      return emphasis === 'strong'
        ? 'var(--ds-table-header-strong-background)'
        : 'var(--ds-table-header-light-background)';
    }
    if (invert) {
      return emphasis === 'strong'
        ? 'var(--ds-table-invert-cell-strong-background)'
        : 'var(--ds-table-invert-cell-light-background)';
    }
    if (this.appearance === 'outlined' && style?.emphasis === undefined) {
      return 'var(--ds-table-outlined-background)';
    }
    return emphasis === 'strong'
      ? 'var(--ds-table-cell-strong-background)'
      : 'var(--ds-table-cell-light-background)';
  }

  cellTextColor(style: DsTableCellStyle | undefined, role: 'header' | 'body'): string {
    if (style?.textColor !== undefined) return style.textColor;
    if (style?.invert === true) return 'var(--ds-table-invert-text-color)';
    return role === 'header' ? this.resolvedHeaderTextColor : this.resolvedCellTextColor;
  }

  keyValueLabelTextColor(style: DsTableCellStyle | undefined): string {
    if (style?.textColor !== undefined || style?.invert === true) {
      return this.cellTextColor(style, 'header');
    }
    return 'var(--ds-table-mobile-key-value-label-text-color)';
  }

  keyValueLabel(header: string): string {
    const label = header.trimEnd();
    return label.endsWith(':') ? label : `${label}:`;
  }

  cellHorizontalAlignment(style: DsTableCellStyle | undefined): DsTableHorizontalAlignment {
    return style?.horizontalAlignment ?? this.horizontalAlignment;
  }

  cellVerticalAlignment(style: DsTableCellStyle | undefined): 'top' | 'middle' | 'bottom' {
    const alignment = style?.verticalAlignment ?? this.verticalAlignment;
    return alignment === 'center' ? 'middle' : alignment;
  }

  cellDividerWidth(
    style: DsTableCellStyle | undefined,
    defaultDivider: boolean,
  ): string {
    const divider = defaultDivider && (style?.divider ?? this.divider);
    return divider ? 'var(--ds-table-border-width)' : '0px';
  }

  cellDividerColor(style: DsTableCellStyle | undefined): string {
    return style?.invert === true
      ? 'var(--ds-table-invert-divider-color)'
      : 'var(--ds-table-divider-color)';
  }

  cellRowHeight(style?: DsTableCellStyle): string {
    return `var(--ds-table-${style?.size ?? this.size}-row-height)`;
  }

  cellPaddingX(style?: DsTableCellStyle): string {
    return `var(--ds-table-${style?.size ?? this.size}-cell-padding-x)`;
  }

  cellPaddingY(style?: DsTableCellStyle): string {
    return `var(--ds-table-${style?.size ?? this.size}-cell-padding-y)`;
  }

  private keyFor(row: T, visibleIndex: number): DsTableKey {
    return this.rowKey(row, this.safePage * this.itemsPerPage + visibleIndex);
  }

  private get normalizedTotalItems(): number {
    return Number.isFinite(this.totalItems) ? Math.max(Math.trunc(this.totalItems), 0) : 0;
  }

  private defaultEmphasis(role: 'header' | 'body'): DsTableCellEmphasis {
    if (this.appearance === 'filled') return role === 'header' ? 'light' : 'strong';
    return role === 'header' ? 'strong' : 'light';
  }

  private syncExpandedKeys(): void {
    const indexOffset = this.paginationMode === 'server'
      ? this.safePage * this.itemsPerPage
      : 0;
    const validKeys = new Set(
      this.rows.map((row, index) => this.rowKey(row, indexOffset + index)),
    );
    for (const key of this.expandedKeys) {
      if (!validKeys.has(key)) this.expandedKeys.delete(key);
    }
    if (this.expandedByDefault) {
      if (this.allowMultipleExpanded) {
        validKeys.forEach(key => this.expandedKeys.add(key));
      } else if (this.expandedKeys.size === 0) {
        const firstKey = validKeys.values().next().value as DsTableKey | undefined;
        if (firstKey !== undefined) this.expandedKeys.add(firstKey);
      }
    }
    if (!this.allowMultipleExpanded && this.expandedKeys.size > 1) {
      const firstExpanded = this.expandedKeys.values().next().value as DsTableKey;
      this.expandedKeys.clear();
      this.expandedKeys.add(firstExpanded);
    }
  }

  private updateCompactLayout(width: number): void {
    if (typeof getComputedStyle === 'undefined') return;
    const breakpointOverride = this.responsiveBreakpoint;
    const breakpoint = breakpointOverride !== null
      && Number.isFinite(breakpointOverride)
      && breakpointOverride >= 0
      ? breakpointOverride
      : Number.parseFloat(
        getComputedStyle(this.host.nativeElement)
          .getPropertyValue('--ds-table-responsive-breakpoint'),
      );
    const next = Number.isFinite(breakpoint) && width < breakpoint;
    if (next === this.compactLayout) return;
    this.compactLayout = next;
    this.cdr.markForCheck();
  }
}
